/**
 * Where each multipart composer upload stands, by upload id: its session,
 * the content identity it began with and the parts already stored. The
 * upload service writes it as it goes; the background upload manager
 * persists the entries a send took with it, so a reload resumes them by
 * part instead of starting over (#4844).
 */
export type UploadSessionIdentity = {
  contentHash: string;
  sizeBytes: number;
  partMd5s: string[];
};

export type UploadSessionJournalEntry = {
  /**
   * The session control URL. Its fragment is the session's bearer token, so
   * it lives only in this page's memory; an entry seeded from browser
   * storage after a reload has none and asks the server for a fresh one.
   */
  uploadUrl?: string;
  /** The session's id: all browser storage keeps of it. */
  sessionId?: string;
  storageKey: string;
  identity?: UploadSessionIdentity;
  completedParts: number[];
};

/** What browser storage may keep of a session: never its bearer token. */
export type PersistableUploadSession = {
  sessionId: string;
  storageKey: string;
  identity?: UploadSessionIdentity;
  completedParts: number[];
};

const SESSION_URL = /^\/api\/browser-upload-sessions\/([a-z0-9]+)#/i;
const SESSION_ID = /^[a-z0-9]+$/i;

/** The session id in a session control URL, without its token. */
export function browserUploadSessionIdOf(uploadUrl: string): string | null {
  return SESSION_URL.exec(uploadUrl)?.[1] ?? null;
}

/** Whether a value has the shape of a browser upload session id. */
export function isBrowserUploadSessionId(value: unknown): value is string {
  return typeof value === 'string' && SESSION_ID.test(value);
}

/**
 * The part of an entry browser storage may keep: the session id, storage key,
 * content identity and stored parts, picked field by field so nothing else
 * (the control URL, its token) can ride along. Null without a session id.
 */
export function persistableUploadSession(
  entry: UploadSessionJournalEntry
): PersistableUploadSession | null {
  const sessionId =
    entry.sessionId ??
    (entry.uploadUrl ? browserUploadSessionIdOf(entry.uploadUrl) : null);
  if (!isBrowserUploadSessionId(sessionId) || !entry.storageKey) return null;
  return {
    sessionId,
    storageKey: entry.storageKey,
    ...(entry.identity
      ? {
          identity: {
            contentHash: entry.identity.contentHash,
            sizeBytes: entry.identity.sizeBytes,
            partMd5s: [...entry.identity.partMd5s],
          },
        }
      : {}),
    completedParts: [...entry.completedParts],
  };
}

type Listener = (
  uploadId: string,
  entry: UploadSessionJournalEntry | null
) => void;

export function createUploadSessionJournal() {
  const entries = new Map<string, UploadSessionJournalEntry>();
  const listeners = new Set<Listener>();
  const emit = (uploadId: string) => {
    const entry = entries.get(uploadId) ?? null;
    for (const listener of listeners) listener(uploadId, entry);
  };
  return {
    get: (uploadId: string) => entries.get(uploadId),
    /** Start (or seed, after a reload) the session an upload sends into. */
    open(uploadId: string, entry: UploadSessionJournalEntry) {
      const sessionId =
        entry.sessionId ??
        (entry.uploadUrl ? browserUploadSessionIdOf(entry.uploadUrl) : null);
      entries.set(uploadId, {
        ...entry,
        ...(sessionId ? { sessionId } : {}),
        completedParts: [...new Set(entry.completedParts)].sort(
          (a, b) => a - b
        ),
      });
      emit(uploadId);
    },
    identified(uploadId: string, identity: UploadSessionIdentity) {
      const entry = entries.get(uploadId);
      if (!entry) return;
      entries.set(uploadId, { ...entry, identity });
      emit(uploadId);
    },
    partDone(uploadId: string, partNumber: number) {
      const entry = entries.get(uploadId);
      if (!entry || entry.completedParts.includes(partNumber)) return;
      entries.set(uploadId, {
        ...entry,
        completedParts: [...entry.completedParts, partNumber].sort(
          (a, b) => a - b
        ),
      });
      emit(uploadId);
    },
    /** The session is finished or unusable; the next attempt opens a new one. */
    close(uploadId: string) {
      if (!entries.delete(uploadId)) return;
      emit(uploadId);
    },
    subscribe(listener: Listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

export type UploadSessionJournal = ReturnType<
  typeof createUploadSessionJournal
>;

/** One journal per page: composer and background uploads share sessions. */
export const uploadSessionJournal = createUploadSessionJournal();
